import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { ErrorNote, SectionTitle, Spinner, fmtDate } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';

interface UserRow { id: string; email: string; display_name: string | null; role: string; status: string; created_at: number }
interface OutboxRow { id: string; engagement_id: string; company_name: string | null; kind: string; status: string; attempts: number; last_error: string | null; created_at: number; synced_at: number | null }
interface HsStatus { configured: boolean; outbox: Record<string, number>; lastError: { last_error: string; updated_at: number } | null }

export function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [outbox, setOutbox] = useState<OutboxRow[]>([]);
  const [hs, setHs] = useState<HsStatus | null>(null);
  const [form, setForm] = useState({ email: '', display_name: '', role: 'consultant', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function load() {
    try {
      const [u, o, h] = await Promise.all([
        api.get<{ users: UserRow[] }>('/api/admin/users'),
        api.get<{ outbox: OutboxRow[] }>('/api/admin/outbox'),
        api.get<HsStatus>('/api/hubspot/status'),
      ]);
      setUsers(u.users);
      setOutbox(o.outbox);
      setHs(h);
    } catch {
      setError('Failed to load admin data (admin role required).');
    }
  }
  useEffect(() => { void load(); }, []);

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/api/admin/users', form);
      setForm({ email: '', display_name: '', role: 'consultant', password: '' });
      setMessage('User created.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus(u: UserRow) {
    await api.patch(`/api/admin/users/${u.id}`, { status: u.status === 'active' ? 'disabled' : 'active' });
    await load();
  }

  async function processOutbox() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await api.post<{ processed: number; failed: number }>('/api/hubspot/process');
      setMessage(`Outbox processed: ${r.processed} synced, ${r.failed} failed.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 503
        ? 'HubSpot token not configured — see docs/SETUP_HUBSPOT.md. Rows stay queued.'
        : 'Processing failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!users) return error ? <ErrorNote message={error} /> : <Spinner />;

  return (
    <div className="space-y-6">
      <div className="card">
        <SectionTitle>HubSpot sync</SectionTitle>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className={`chip ${hs?.configured ? 'border-green-700 bg-green-950/40 text-green-300' : 'border-amber-700 bg-amber-950/40 text-amber-300'}`}>
            {hs?.configured ? 'Token configured' : 'Token not configured'}
          </span>
          <span className="text-slate-400">
            Queue: {hs?.outbox?.pending ?? 0} pending · {hs?.outbox?.synced ?? 0} synced · {hs?.outbox?.failed ?? 0} failed
          </span>
          <button className="btn-secondary !px-3 !py-1.5 text-xs" disabled={busy} onClick={() => void processOutbox()}>
            ⟳ Process outbox now
          </button>
        </div>
        {!hs?.configured && (
          <p className="mt-2 text-xs text-slate-500">
            Set the secret: <code className="text-brand-300">npx wrangler secret put HUBSPOT_ACCESS_TOKEN</code> — setup
            steps in <code>docs/SETUP_HUBSPOT.md</code>. Intake keeps working; submissions queue here meanwhile.
          </p>
        )}
        {hs?.lastError && <p className="mt-2 text-xs text-red-400">Last error: {hs.lastError.last_error}</p>}
        {message && <p className="mt-2 text-xs text-green-400">{message}</p>}
        {error && <ErrorNote message={error} />}
      </div>

      <div className="card">
        <SectionTitle>Users</SectionTitle>
        <form onSubmit={createUser} className="mb-4 grid gap-3 sm:grid-cols-5">
          <input className="input" type="email" placeholder="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <input className="input" placeholder="display name" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
          <select className="input" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
            <option value="consultant">consultant</option>
            {user?.role === 'superadmin' && <option value="admin">admin</option>}
          </select>
          <input className="input" type="text" placeholder="password (12+ chars)" required value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
          <button className="btn-primary" disabled={busy}>Create user</button>
        </form>
        <table className="w-full">
          <thead className="bg-slate-900">
            <tr><th className="th">Email</th><th className="th">Name</th><th className="th">Role</th><th className="th">Status</th><th className="th">Created</th><th className="th"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {users.map((u) => (
              <tr key={u.id} className="bg-slate-950/40">
                <td className="td">{u.email}</td>
                <td className="td">{u.display_name ?? '—'}</td>
                <td className="td"><span className="chip border-slate-700 text-slate-300">{u.role}</span></td>
                <td className="td">
                  <span className={`chip ${u.status === 'active' ? 'border-green-700 text-green-300' : 'border-red-800 text-red-300'}`}>{u.status}</span>
                </td>
                <td className="td text-xs">{fmtDate(u.created_at)}</td>
                <td className="td">
                  {u.role !== 'superadmin' && (
                    <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => void toggleStatus(u)}>
                      {u.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <SectionTitle>HubSpot outbox (latest 100)</SectionTitle>
        {outbox.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">Outbox is empty.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900">
                <tr><th className="th">Company</th><th className="th">Kind</th><th className="th">Status</th><th className="th">Attempts</th><th className="th">Error</th><th className="th">Created</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {outbox.map((o) => (
                  <tr key={o.id} className="bg-slate-950/40">
                    <td className="td">{o.company_name ?? o.engagement_id}</td>
                    <td className="td text-xs">{o.kind}</td>
                    <td className="td">
                      <span className={`chip ${o.status === 'synced' ? 'border-green-700 text-green-300' : o.status === 'failed' ? 'border-red-800 text-red-300' : 'border-amber-700 text-amber-300'}`}>{o.status}</span>
                    </td>
                    <td className="td text-xs">{o.attempts}</td>
                    <td className="td max-w-64 truncate text-xs text-red-400">{o.last_error ?? ''}</td>
                    <td className="td text-xs">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
